import { actionVerbForResource } from "@shared/action-verb";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, Flag, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { usePreviewPortal } from "@/components/portal/preview-context";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { portalRpc } from "@/lib/portal-rpc";
import { usePortalContextOptional } from "./portal-shell";

type SidebarMilestone = {
  day: number;
  label: string;
  resourceCount: number;
  todoCount: number;
  itemCount: number;
  completedCount: number;
};

export function PortalJourney() {
  const portalCtx = usePortalContextOptional();
  const preview = usePreviewPortal();
  const isPreview = preview !== null;

  const today = useQuery({
    ...portalRpc.getToday.queryOptions(),
    enabled: !isPreview,
  });
  const sidebar = useQuery({
    ...portalRpc.getSidebar.queryOptions(),
    enabled: !isPreview,
  });

  const [activeDayLocal, setActiveDayLocal] = useState<number | null>(null);
  const activeDay = isPreview ? preview!.milestoneSelected : activeDayLocal;
  const setActiveDay = isPreview
    ? (day: number) => preview!.setMilestoneSelected(day)
    : (day: number) => setActiveDayLocal(day);

  // Auto-select the most relevant milestone on first load: exact match for
  // currentDay, else the latest milestone <= currentDay, else the first.
  const milestonesData = isPreview
    ? preview!.sidebar.myJourney.milestones
    : sidebar.data?.myJourney.milestones;
  const currentDayData = isPreview
    ? preview!.today.currentDay
    : today.data?.ok
      ? today.data.payload.currentDay
      : 0;
  useEffect(() => {
    if (isPreview) return;
    if (activeDayLocal !== null) return;
    if (!milestonesData || milestonesData.length === 0) return;
    const exact = milestonesData.find((m) => m.day === currentDayData);
    if (exact) {
      setActiveDayLocal(exact.day);
      return;
    }
    const previous = [...milestonesData].reverse().find((m) => m.day <= currentDayData);
    setActiveDayLocal(previous?.day ?? milestonesData[0].day);
  }, [isPreview, activeDayLocal, milestonesData, currentDayData]);

  if (!isPreview && (sidebar.isLoading || today.isLoading)) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const milestones: SidebarMilestone[] = isPreview
    ? preview!.sidebar.myJourney.milestones
    : (sidebar.data?.myJourney.milestones ?? []);
  const todayPayload = !isPreview && today.data?.ok ? today.data.payload : null;
  const welcomeMessages: Record<string, string> = isPreview
    ? preview!.today.welcomeMessages
    : (todayPayload?.welcomeMessages ?? {});
  const welcomeMessage = activeDay !== null ? (welcomeMessages[String(activeDay)] ?? null) : null;
  const firstName = isPreview
    ? preview!.sidebar.hireFirstName
    : (todayPayload?.hireFirstName ?? portalCtx?.hireFirstName ?? "");
  const currentDay = currentDayData;
  const totalDays = milestones.length > 0 ? milestones[milestones.length - 1].day : 60;
  const progressPct = Math.max(0, Math.min(100, Math.round((currentDay / totalDays) * 100)));
  const readOnly = isPreview ? true : (portalCtx?.readOnly ?? false);

  const hideWelcomeHeader = preview?.hideWelcomeHeader ?? false;

  return (
    <div className="mx-auto w-full max-w-3xl">
      {!hideWelcomeHeader && (
        <>
          {/* Eyebrow */}
          <div
            className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.5px]"
            style={{ color: "hsl(var(--secondary-foreground))" }}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--accent-brand)" }}
            />
            Dia {currentDay} de {totalDays} · {progressPct}% concluído
          </div>

          {/* H1 */}
          <h1 className="mt-3 text-[24px] font-medium tracking-[-0.3px] text-foreground">
            Boas-vindas, {firstName} 👋
          </h1>
        </>
      )}

      {welcomeMessage ? (
        <p
          className={
            "max-w-[620px] whitespace-pre-line text-[13px] leading-[1.7] text-muted-foreground " +
            (hideWelcomeHeader ? "" : "mt-4")
          }
          data-testid="portal-welcome-message"
        >
          {welcomeMessage}
        </p>
      ) : null}

      {/* Divider */}
      {!hideWelcomeHeader && <div className="my-8 h-px bg-border" />}

      {/* Subtitle */}
      <div>
        <h2 className="text-[14px] font-medium text-foreground">Sua jornada</h2>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Clique em um marco para ver o que esperamos.
        </p>
      </div>

      {milestones.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <p className="text-sm font-medium">Sua jornada ainda está sendo preparada.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Volte em instantes — seu time está montando os marcos.
          </p>
        </Card>
      ) : (
        <>
          <div className="mt-[22px]">
            <Metro
              milestones={milestones}
              currentDay={currentDay}
              activeDay={activeDay}
              onSelect={setActiveDay}
            />
          </div>

          <div className="mt-6">
            {activeDay === null ? (
              <div className="rounded-lg bg-secondary px-5 py-6 text-center text-[13px] text-muted-foreground">
                Selecione um marco acima para ver os to-dos.
              </div>
            ) : (
              <MilestoneInlineDetail day={activeDay} readOnly={readOnly} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

type MilestoneState = "completed" | "current" | "selected" | "future";

/**
 * Fill % of the track. Snaps to the visual position of the last
 * completed/current milestone so the line clearly connects filled dots.
 * Dots are equally spaced (flex justify-between), so we interpolate by
 * visual index, not by day number.
 */
function computeFillPct(milestones: SidebarMilestone[], currentDay: number): number {
  const n = milestones.length;
  if (n <= 1 || currentDay <= 0) return 0;

  let lastFilledIdx = -1;
  for (let i = 0; i < n; i++) {
    if (milestones[i].day <= currentDay) lastFilledIdx = i;
  }
  if (lastFilledIdx <= 0) return 0;
  return Math.min(100, (lastFilledIdx / (n - 1)) * 100);
}

function getMilestoneState(
  day: number,
  currentDay: number,
  activeDay: number | null,
): MilestoneState {
  if (activeDay !== null && day === activeDay && day !== currentDay) return "selected";
  if (day === currentDay) return "current";
  if (day < currentDay) return "completed";
  return "future";
}

function Metro({
  milestones,
  currentDay,
  activeDay,
  onSelect,
}: {
  milestones: SidebarMilestone[];
  currentDay: number;
  activeDay: number | null;
  onSelect: (day: number) => void;
}) {
  const lastIndex = milestones.length - 1;
  const fillPct = computeFillPct(milestones, currentDay);

  return (
    <div className="relative pb-2 pt-2">
      {/* Tracks (behind dots) */}
      <div
        className="absolute left-[18px] right-[18px] h-[2px]"
        style={{ top: "calc(0.5rem + 17px)", background: "hsl(var(--secondary))", zIndex: 0 }}
      />
      <div
        className="absolute left-[18px] h-[2px] transition-[width] duration-500"
        style={{
          top: "calc(0.5rem + 17px)",
          background: "var(--accent-brand)",
          width: `calc((100% - 36px) * ${fillPct} / 100)`,
          zIndex: 0,
        }}
      />

      <div className="relative flex items-start justify-between gap-2" style={{ zIndex: 1 }}>
        {milestones.map((m, idx) => {
          const state = getMilestoneState(m.day, currentDay, activeDay);
          const isLast = idx === lastIndex;
          return (
            <button
              key={m.day}
              type="button"
              onClick={() => onSelect(m.day)}
              className="group flex flex-col items-center gap-2 focus:outline-none"
              data-testid={`journey-milestone-${m.day}`}
              aria-current={state === "current" ? "step" : undefined}
            >
              <MilestoneDot state={state} day={m.day} isLast={isLast} />
              <span
                className={
                  "text-[11px] " +
                  (state === "current" || state === "selected"
                    ? "font-medium text-foreground"
                    : "text-muted-foreground")
                }
              >
                {m.label}
              </span>
              <span className="text-[10px] tabular-nums text-muted-foreground/80">
                {m.itemCount === 0 ? "—" : `${m.completedCount}/${m.itemCount}`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MilestoneDot({
  state,
  day,
  isLast,
}: {
  state: MilestoneState;
  day: number;
  isLast: boolean;
}) {
  const styles: Record<MilestoneState, React.CSSProperties> = {
    completed: {
      background: "var(--accent-brand)",
      borderColor: "var(--accent-brand)",
      color: "#fff",
    },
    current: {
      background: "var(--accent-brand)",
      borderColor: "var(--accent-brand)",
      color: "#fff",
      boxShadow: "0 0 0 4px var(--accent-brand-bg)",
    },
    selected: {
      background: "var(--surface-dark)",
      borderColor: "var(--surface-dark)",
      color: "#fff",
    },
    future: {
      background: "hsl(var(--background))",
      borderColor: "hsl(var(--border))",
      color: "hsl(var(--muted-foreground))",
    },
  };
  return (
    <span
      className="flex h-9 w-9 items-center justify-center rounded-full border-[1.5px] text-[11px] font-semibold transition-all"
      style={styles[state]}
    >
      {isLast ? <Flag className="h-4 w-4" strokeWidth={2} /> : day}
    </span>
  );
}

type MilestoneAssignedResource = {
  resourceId: string;
  title: string;
  renderType: string | null;
  behavior: string;
  sourceUrl: string | null;
  actionLabel?: string;
  opened: boolean;
  completed: boolean;
};

type MilestoneDueTodo = {
  todoId: string;
  title: string;
  description: string | null;
  link: string | null;
  done: boolean;
  targetDate: string | null;
};

function formatTodoDeadline(iso: string | null): string | null {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  // Compare BRT calendar dates — same offset rule used on the server.
  const targetBrt = new Date(target.getTime() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const todayBrt = new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const tomorrowBrt = new Date(Date.now() + 24 * 60 * 60 * 1000 - 3 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  if (targetBrt === todayBrt) return "Hoje";
  if (targetBrt === tomorrowBrt) return "Amanhã";
  const [, m, d] = targetBrt.split("-");
  return `${d}/${m}`;
}

function MilestoneInlineDetail({ day, readOnly }: { day: number; readOnly: boolean }) {
  const preview = usePreviewPortal();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryOpts = portalRpc.getMilestone.queryOptions({ input: { day } });
  const milestone = useQuery({ ...queryOpts, enabled: preview === null });
  const milestoneKey = queryOpts.queryKey;

  const toggleCompletion = useMutation({
    ...portalRpc.toggleResourceCompletion.mutationOptions(),
    onMutate: async ({ resourceId, completed }) => {
      await queryClient.cancelQueries({ queryKey: milestoneKey });
      const prev = queryClient.getQueryData<typeof milestone.data>(milestoneKey);
      if (prev) {
        queryClient.setQueryData(milestoneKey, {
          ...prev,
          assignedResources: (prev.assignedResources ?? []).map((r) =>
            r.resourceId === resourceId ? { ...r, completed } : r,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctxPrev) => {
      if (ctxPrev?.prev) queryClient.setQueryData(milestoneKey, ctxPrev.prev);
      toast({ title: "Não foi possível atualizar.", variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: milestoneKey }),
  });

  const toggleTodo = useMutation({
    ...portalRpc.toggleTodo.mutationOptions(),
    onMutate: async ({ todoId }) => {
      await queryClient.cancelQueries({ queryKey: milestoneKey });
      const prev = queryClient.getQueryData<typeof milestone.data>(milestoneKey);
      if (prev) {
        queryClient.setQueryData(milestoneKey, {
          ...prev,
          dueTodos: (prev.dueTodos ?? []).map((t) =>
            t.todoId === todoId ? { ...t, done: !t.done } : t,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctxPrev) => {
      if (ctxPrev?.prev) queryClient.setQueryData(milestoneKey, ctxPrev.prev);
      toast({ title: "Não foi possível atualizar.", variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: milestoneKey }),
  });

  if (preview === null && milestone.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (preview === null && (milestone.error || !milestone.data)) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">
          Não foi possível carregar os to-dos deste marco.
        </p>
      </Card>
    );
  }

  const data = preview ? preview.milestone : milestone.data!;
  const resources: MilestoneAssignedResource[] = data.assignedResources ?? [];
  const todos: MilestoneDueTodo[] = (data.dueTodos ?? []).map((t) => ({
    todoId: t.todoId,
    title: t.title,
    description: t.description,
    link: t.link,
    done: t.done,
    targetDate: t.targetDate ?? null,
  }));

  return (
    <section className="space-y-3">
      <div className="text-[11px] font-medium uppercase tracking-[0.5px] text-muted-foreground">
        To-dos para o {data.label?.trim() ? data.label : `Dia ${data.day}`}
      </div>

      {resources.length === 0 && todos.length === 0 ? (
        <EmptyState day={data.day} />
      ) : (
        <div className="space-y-2">
          {resources.map((r) => (
            <ResourceRow
              key={r.resourceId}
              resource={r}
              readOnly={readOnly}
              isPending={
                toggleCompletion.isPending &&
                toggleCompletion.variables?.resourceId === r.resourceId
              }
              onToggle={(completed) =>
                toggleCompletion.mutate({ resourceId: r.resourceId, completed })
              }
            />
          ))}
          {todos.map((t) => (
            <TodoRow
              key={t.todoId}
              todo={t}
              readOnly={readOnly}
              isPending={toggleTodo.isPending && toggleTodo.variables?.todoId === t.todoId}
              onToggle={() => toggleTodo.mutate({ todoId: t.todoId })}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function TodoRow({
  todo,
  readOnly,
  isPending,
  onToggle,
}: {
  todo: MilestoneDueTodo;
  readOnly: boolean;
  isPending: boolean;
  onToggle: () => void;
}) {
  const titleClass =
    "text-[13px] font-medium transition-colors " +
    (todo.done ? "text-muted-foreground line-through" : "text-foreground");
  const deadlineLabel = formatTodoDeadline(todo.targetDate);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-card px-4 py-3 min-h-[56px]">
      <button
        type="button"
        disabled={readOnly || isPending}
        onClick={onToggle}
        className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded transition disabled:opacity-50"
        style={
          todo.done
            ? { background: "var(--success)", borderColor: "var(--success)", color: "#fff" }
            : { background: "hsl(var(--background))", border: "1.5px solid hsl(var(--border))" }
        }
        aria-label={todo.done ? "Desmarcar como concluído" : "Marcar como concluído"}
        data-testid={`todo-toggle-${todo.todoId}`}
      >
        {todo.done && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
      <div className="flex-1 flex flex-col gap-0.5">
        <span className={titleClass}>{todo.title}</span>
        {todo.description ? (
          <span className="text-[12px] text-muted-foreground">{todo.description}</span>
        ) : null}
        {deadlineLabel ? (
          <span className="mt-1 inline-flex w-fit rounded-full border border-border/60 bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            {deadlineLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ResourceRow({
  resource,
  readOnly,
  isPending,
  onToggle,
}: {
  resource: MilestoneAssignedResource;
  readOnly: boolean;
  isPending: boolean;
  onToggle: (completed: boolean) => void;
}) {
  const actionLabel =
    resource.actionLabel ?? actionVerbForResource(resource.renderType, resource.behavior);
  const titleClass =
    "flex-1 text-[13px] font-medium transition-colors " +
    (resource.completed ? "text-muted-foreground line-through" : "text-foreground");
  const label = actionLabel ? `${actionLabel}: ${resource.title}` : resource.title;

  const preview = usePreviewPortal();
  const previewNavigate = preview?.onResourceNavigate;
  const isTodo = resource.renderType === "todo";
  const isRedirect = resource.behavior === "redirect" && resource.sourceUrl;
  const isClickable = !isTodo;

  const inner = (
    <div
      className={
        "group flex items-center gap-3 rounded-lg border border-border/60 bg-card px-4 py-3 min-h-[56px]" +
        (isClickable ? " hover:bg-accent/40 cursor-pointer" : "")
      }
    >
      <button
        type="button"
        disabled={readOnly || isPending}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(!resource.completed);
        }}
        className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded transition disabled:opacity-50"
        style={
          resource.completed
            ? { background: "var(--success)", borderColor: "var(--success)", color: "#fff" }
            : { background: "hsl(var(--background))", border: "1.5px solid hsl(var(--border))" }
        }
        aria-label={resource.completed ? "Desmarcar como concluído" : "Marcar como concluído"}
        data-testid={`resource-toggle-${resource.resourceId}`}
      >
        {resource.completed && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
      <span className={titleClass}>{label}</span>
      {isClickable ? (
        <ChevronRight
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground"
          strokeWidth={2}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  if (isTodo) return inner;

  if (isRedirect) {
    return (
      <a href={resource.sourceUrl!} target="_blank" rel="noreferrer" className="block no-underline">
        {inner}
      </a>
    );
  }

  if (previewNavigate) {
    return (
      <button
        type="button"
        onClick={() => previewNavigate(resource.resourceId)}
        className="block w-full text-left no-underline"
      >
        {inner}
      </button>
    );
  }

  return (
    <Link href={`/onboarding/content/${resource.resourceId}`} className="block no-underline">
      {inner}
    </Link>
  );
}

function EmptyState({ day }: { day: number }) {
  return (
    <div className="rounded-lg bg-secondary px-5 py-[18px]">
      <div className="text-[13px] font-medium text-foreground">
        Nada planejado para o Dia {day} ainda
      </div>
      <p className="mt-1 text-[12px] leading-[1.6] text-muted-foreground">
        Esse marco ainda está sendo desenhado.
      </p>
    </div>
  );
}
